import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LockIcon from "@mui/icons-material/Lock";
import Radio from "designSystem/components/checkbox/Radio";
import { black, grey } from "designSystem/styles/color";
import { FlexColumn, FlexRowCenter, OverflowText } from "designSystem/index";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent } from "react";
import { ChannelItem } from "src/Chat/api";
import { ChannelMembershipType } from "../api";
import { useMSTeamsStore } from "./MSTeamsStore";

const PREFIX = "ChannelInfo";

const classes = {
	channelBox: `${PREFIX}channelBox`,
	channelInnerContainer: `${PREFIX}channelInnerContainer`,
	channelOuterContainer: `${PREFIX}channelOuterContainer`,
	channelSelected: `${PREFIX}channelSelected`,
	lockIcon: `${PREFIX}lockIcon`,
};

const StyledFlexRowCenter = styled(FlexRowCenter)(({
	theme,
}) => ({
	[`& .${classes.channelBox}`]: {
		flexGrow: 1,
		justifyContent: "space-evenly",
		// force truncation of OverflowText components and allocate space for ChevronRightIcon or Radio + 8px of padding
		maxWidth: "calc(100% - 24px)",
		padding: "0 8px",
	},

	[`& .${classes.channelInnerContainer}`]: {
		flexGrow: 1,
		height: "100%",
		minWidth: 0,
	},

	[`&.${classes.channelOuterContainer}`]: {
		cursor: "pointer",
		height: 50,
		padding: 8,
		"&:hover": {
			backgroundColor: grey[200],
		},
		"&:active": {
			backgroundColor: theme.palette.HighlightSelected,
		},
	},

	[`&.${classes.channelSelected}`]: {
		backgroundColor: theme.palette.HighlightSelected,
	},

	[`& .${classes.lockIcon}`]: {
		color: black[100],
		marginRight: 4,
	},
}));

interface ChannelInfoProps {
	/** the current channel object */
	channel: ChannelItem<ChannelMembershipType>;

	/** radio button flag */
	showRadio?: boolean;
}

const ChannelInfo: FunctionComponent<ChannelInfoProps> = observer(({ channel, showRadio }) => {
	const { displayName, membershipType, selected, webUrl } = channel;
	const {
		channelBox,
		channelInnerContainer,
		channelOuterContainer,
		channelSelected,
		lockIcon,
	} = classes;

	const { getTeamName, selectAndNavigateToChannelDisplay, onSelectChannel } = useMSTeamsStore();
	const onClickChannel = showRadio ? () => onSelectChannel(channel) : () => selectAndNavigateToChannelDisplay(channel);
	const channelOuterContainerClasses = selected ? [channelOuterContainer, channelSelected].join(" ") : channelOuterContainer;
	const teamName = getTeamName(webUrl);

	return (
		<StyledFlexRowCenter
			className={channelOuterContainerClasses}
			onClick={onClickChannel}
		>
			{showRadio && (
				<FlexRowCenter m={1}>
					<Radio checked={selected} />
				</FlexRowCenter>
			)}
			<FlexRowCenter className={channelInnerContainer}>
				<FlexColumn className={channelBox}>
					<FlexRowCenter>
						{membershipType === ChannelMembershipType.private &&
							<LockIcon className={lockIcon} />
						}
						<OverflowText text={displayName} />
					</FlexRowCenter>
					<FlexRowCenter>
						<OverflowText
							size={10}
							text={teamName}
						/>
					</FlexRowCenter>
				</FlexColumn>
				{!showRadio &&
					<ChevronRightIcon />
				}
			</FlexRowCenter>
		</StyledFlexRowCenter>
	);
});

export default ChannelInfo;